import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faDownload,
  faRefresh,
  faSearch,
  faTimes,
  faUser,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import {
  getCreditAdviseUploadHistory,
  getCreditAdviseProcessedRows,
  exportCreditAdviseProcessedRows,
} from "../../services/settlementApi";

const CreditAdviseUploadProcessedPage = () => {
  const navigate = useNavigate();
  const { uploadId } = useParams();

  const [uploadInfo, setUploadInfo] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowType, setRowType] = useState("all");
  const [exporting, setExporting] = useState(false);

  const [filterPaymentRef, setFilterPaymentRef] = useState("");
  const [filterTransactionId, setFilterTransactionId] = useState("");
  const [filterTransactionDate, setFilterTransactionDate] = useState("");
  const [filterBeneficiary, setFilterBeneficiary] = useState("");
  const [filterResultStatus, setFilterResultStatus] = useState("");
  const [filterDetails, setFilterDetails] = useState("");

  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  useEffect(() => {
    const fetchUploadInfo = async () => {
      try {
        const response = await getCreditAdviseUploadHistory({ page: 1, limit: 1000 });
        const upload = response.data.data?.find((u) => u.upload_id === uploadId);
        if (upload) {
          setUploadInfo(upload);
        } else {
          toast.error("Upload not found");
          navigate("/credit-advise-history");
        }
      } catch (error) {
        toast.error("Failed to fetch upload info");
        console.error(error);
      }
    };

    if (uploadId) fetchUploadInfo();
  }, [uploadId, navigate]);

  const fetchRows = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    try {
      const response = await getCreditAdviseProcessedRows(uploadId, {
        page: 1,
        limit: 5000,
        type: rowType,
      });
      const data = response.data.data || [];
      setAllRows(data);
      setPage(1);
    } catch (error) {
      toast.error("Failed to fetch processed rows");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [uploadId, rowType]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filteredRows = useMemo(() => {
    const match = (val, filter) => !filter || String(val ?? "").toLowerCase().includes(filter.toLowerCase());
    return allRows.filter((row) => {
      if (!match(row.payment_ref_no, filterPaymentRef)) return false;
      if (!match(row.transaction_id, filterTransactionId)) return false;
      if (!match(row.transaction_date, filterTransactionDate)) return false;
      const beneficiaryText = `${row.beneficiary_name ?? ""} ${row.beneficiary_account_number ?? ""} ${row.beneficiary_bank ?? ""}`;
      if (!match(beneficiaryText, filterBeneficiary)) return false;
      if (!match(row.result_type, filterResultStatus)) return false;
      const detailsText = row.error_message || row.processed_by || "";
      if (!match(detailsText, filterDetails)) return false;
      return true;
    });
  }, [
    allRows,
    filterPaymentRef,
    filterTransactionId,
    filterTransactionDate,
    filterBeneficiary,
    filterResultStatus,
    filterDetails,
  ]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));

  const hasActiveFilters =
    filterPaymentRef ||
    filterTransactionId ||
    filterTransactionDate ||
    filterBeneficiary ||
    filterResultStatus ||
    filterDetails;

  const handleClearFilters = () => {
    setFilterPaymentRef("");
    setFilterTransactionId("");
    setFilterTransactionDate("");
    setFilterBeneficiary("");
    setFilterResultStatus("");
    setFilterDetails("");
    setPage(1);
  };

  const handleExport = async (exportType) => {
    if (!uploadId) return;
    setExporting(true);
    try {
      const response = await exportCreditAdviseProcessedRows(uploadId, { type: exportType });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `credit_advise_${uploadId}_${exportType}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${exportType} rows successfully`);
    } catch (error) {
      toast.error("Export failed");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const getResultBadge = (resultType) => {
    if (resultType === "success") {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Success</span>;
    }
    return <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">Failed</span>;
  };

  const formatDateTime = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (value) => {
    const text = String(value ?? "").trim();
    if (!text) return "—";
    if (text.toUpperCase().startsWith("PHP")) return text;
    return `PHP ${text}`;
  };

  if (!uploadInfo) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading upload information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate("/credit-advise-history")}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg flex items-center gap-2 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Credit Advise CSV Processed</h1>
            <p className="text-gray-600 text-sm mt-0.5">{uploadInfo.original_file_name}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Rows</p>
              <p className="text-2xl font-bold text-gray-800">{uploadInfo.total_rows || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Success</p>
              <p className="text-2xl font-bold text-green-600">{uploadInfo.success_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Failed</p>
              <p className="text-2xl font-bold text-red-600">{uploadInfo.failed_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <p
                className={`text-lg font-bold ${
                  uploadInfo.status === "completed"
                    ? "text-green-600"
                    : uploadInfo.status === "failed"
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              >
                {(uploadInfo.status || "").charAt(0).toUpperCase() + (uploadInfo.status || "").slice(1)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded By</p>
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                {uploadInfo.uploaded_by_name || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded</p>
              <p className="text-xs text-gray-700">{formatDateTime(uploadInfo.created_at)}</p>
            </div>
          </div>

          {uploadInfo.error_message && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {uploadInfo.error_message}
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => {
                  setRowType("all");
                  setPage(1);
                }}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  rowType === "all" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                All Rows ({uploadInfo.total_rows || 0})
              </button>
              <button
                onClick={() => {
                  setRowType("success");
                  setPage(1);
                }}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  rowType === "success" ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Success ({uploadInfo.success_count || 0})
              </button>
              <button
                onClick={() => {
                  setRowType("failed");
                  setPage(1);
                }}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  rowType === "failed" ? "bg-red-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Failed ({uploadInfo.failed_count || 0})
              </button>
              <button
                onClick={fetchRows}
                disabled={loading}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleExport("all")}
                disabled={exporting || (uploadInfo.total_rows || 0) === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} /> Export All
              </button>
              <button
                onClick={() => handleExport("failed")}
                disabled={exporting || (uploadInfo.failed_count || 0) === 0}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} /> Export Failed
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
              Filter Rows (frontend)
            </p>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faTimes} /> Clear Filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Payment Ref No</label>
              <input
                type="text"
                value={filterPaymentRef}
                onChange={(e) => {
                  setFilterPaymentRef(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Transaction ID</label>
              <input
                type="text"
                value={filterTransactionId}
                onChange={(e) => {
                  setFilterTransactionId(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Transaction Date</label>
              <input
                type="text"
                value={filterTransactionDate}
                onChange={(e) => {
                  setFilterTransactionDate(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Beneficiary</label>
              <input
                type="text"
                value={filterBeneficiary}
                onChange={(e) => {
                  setFilterBeneficiary(e.target.value);
                  setPage(1);
                }}
                placeholder="Name / account / bank..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Result</label>
              <select
                value={filterResultStatus}
                onChange={(e) => {
                  setFilterResultStatus(e.target.value);
                  setPage(1);
                }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Details</label>
              <input
                type="text"
                value={filterDetails}
                onChange={(e) => {
                  setFilterDetails(e.target.value);
                  setPage(1);
                }}
                placeholder="Error or processor..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <p className="text-xs text-blue-600 mt-2">
              Showing {filteredRows.length} of {allRows.length} rows (filtered)
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs lg:text-sm">
              <thead className="bg-gray-100 border-b border-gray-300 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Row #</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Payment Ref No</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Transaction ID</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Transaction Date</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Beneficiary Name</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Beneficiary Account #</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Beneficiary Bank</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">Amount</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Generated File</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Result</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-3 py-8 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-3 py-8 text-center text-gray-500">No rows found</td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr key={`${row.id}-${idx}`} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-700 font-medium">{row.row_number}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.payment_ref_no || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.transaction_id || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.transaction_date || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.beneficiary_name || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.beneficiary_account_number || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.beneficiary_bank || "—"}</td>
                      <td className="px-3 py-2.5 text-right text-gray-800">{formatAmount(row.amount)}</td>
                      <td className="px-3 py-2.5 text-gray-800">
                        {row.result_type === "success" ? (
                          <div className="flex items-center gap-2">
                            <span className="truncate max-w-[180px]" title={row.generated_file_name || ""}>
                              {row.generated_file_name || "—"}
                            </span>
                            {row.public_share_url && (
                              <a
                                href={row.public_share_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
                                title="Open public share URL"
                              >
                                <FontAwesomeIcon icon={faLink} />
                              </a>
                            )}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2.5">{getResultBadge(row.result_type)}</td>
                      <td className="px-3 py-2.5 text-xs max-w-xs">
                        {row.error_message ? (
                          <div className="text-red-700 truncate" title={row.error_message}>
                            {row.error_message}
                          </div>
                        ) : (
                          <span className="text-gray-500">
                            Processed by {row.processed_by || "—"}
                            {row.settlement_id ? ` (SR# ${row.settlement_id})` : ""}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredRows.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, filteredRows.length)} of {filteredRows.length} rows
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-sm font-medium text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CreditAdviseUploadProcessedPage;
